import { GeneralService, ApiKeyOrGetter } from "./GeneralService";

export interface CentroidDistances {
  mean: number;
  std: number;
  p5: number;
  p25: number;
  p50: number;
  p75: number;
  p95: number;
  histogram_bins: number[];
  histogram_counts: number[];
}

export interface ExampleOodPoint {
  request_id: string;
  distance: number;
  summary: string;
  tags: string[];
}

export type CLRunStatus =
  | "PENDING"
  | "ANALYZING"
  | "ANALYZED"
  | "ACTIONING"
  | "ACTIONED"
  | "DISMISSED"
  | "FAILED";

export const NON_TERMINAL_STATUSES: CLRunStatus[] = [
  "PENDING",
  "ANALYZING",
  "ACTIONING",
];

export type ScopeType = "INTENT_GROUP" | "INTENT";

export interface ContinuousLearningRun {
  id: number;
  company_id: number;
  intent_group_id?: number;
  application_id?: number;
  intent_id?: number;
  intent_group_name?: string;
  intent_name?: string;
  application_name?: string;
  source_finetune_run_id: number;
  model_profile_id?: number;
  date_window_start: string;
  date_window_end: string;
  total_prod_points: number;
  ood_count: number;
  ood_threshold: number;
  status: CLRunStatus;
  psi?: number;
  wasserstein?: number;
  novel_count: number;
  drift_detected: boolean;
  prod_centroid_distances?: CentroidDistances;
  tag_summary?: Record<string, number>;
  tag_run_ids?: number[];
  example_ood_points?: ExampleOodPoint[];
  ood_request_ids?: string[];
  recommendation: "NONE" | "FINETUNE_RECOMMENDED";
  recommendation_reason?: string;
  synthetic_gap: number;
  action_taken: "PENDING" | "ACTIONED" | "DISMISSED";
  triggered_dataset_id?: number;
  triggered_train_dataset_id?: number;
  triggered_test_set_id?: number;
  triggered_generation_dataset_id?: number;
  triggered_composition_id?: number;
  triggered_finetune_run_id?: number;
  error?: string;
  error_message?: string;
  date_created: string;
  d_created?: number;
}

export interface EligibleModel {
  id: number;
  company_id: number;
  intent_group_id?: number;
  application_id?: number;
  intent_id?: number;
  model: string;
  finetune_run_id: number;
  model_profile_id: number;
  profile_status: string;
  intent_group_name?: string;
  intent_name?: string;
  application_name?: string;
  company_name: string;
  dataset_composition_id: number;
  base_model: string;
  scope_type: ScopeType;
}

export interface CompositionMember {
  dataset_id: number;
  dataset_name: string;
  dataset_type: string;
  request_count: number;
  source: "Original" | "New (OOD)" | "Synthetic";
}

export interface ActionSummaryTestSet {
  id: number;
  name: string;
  is_golden: boolean;
  intent_group_id?: number;
  request_count: number;
  type: string;
}

export interface ActionSummary {
  composition: {
    id: number;
    name: string;
    parent_composition_id: number | null;
    members: CompositionMember[];
  } | null;
  test_sets: ActionSummaryTestSet[];
  source_finetune_run: {
    id: number;
    base_model: string;
    prompt: string;
    display_name: string;
    description: string | null;
    fourbit: boolean;
    prompt_deletions: string[];
    dataset_composition_id: number | null;
    status: string;
    meta: Record<string, unknown> | null;
    [key: string]: unknown;
  } | null;
  triggered_finetune_run_id: number | null;
}

export class ImprovementsService {
  public static getEligibleModels(
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: ApiKeyOrGetter,
    companyId?: number
  ) {
    const params = new URLSearchParams();
    if (companyId !== undefined) {
      params.set("company_id", String(companyId));
    }
    const qs = params.toString();
    return GeneralService.get(
      `/continuous_learning/models${qs ? `?${qs}` : ""}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getModelById(
    modelId: number,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/continuous_learning/models/${modelId}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getRuns(
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: ApiKeyOrGetter,
    scope?: {
      intentGroupId?: number;
      applicationId?: number;
      intentId?: number;
    },
    limit: number = 50,
    offset: number = 0
  ) {
    const params = new URLSearchParams();
    params.set("limit", String(limit));
    params.set("offset", String(offset));
    if (scope?.intentGroupId != null) {
      params.set("intent_group_id", String(scope.intentGroupId));
    } else if (scope?.applicationId != null && scope?.intentId != null) {
      params.set("application_id", String(scope.applicationId));
      params.set("intent_id", String(scope.intentId));
    }
    return GeneralService.get(
      `/continuous_learning/runs?${params.toString()}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getRun(
    runId: number,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/continuous_learning/runs/${runId}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static trigger(
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: ApiKeyOrGetter,
    scope?: {
      intentGroupId?: number;
      applicationId?: number;
      intentId?: number;
    },
    minOod: number = 1000,
    dateStart?: string,
    dateEnd?: string
  ) {
    const body: Record<string, unknown> = { min_ood: minOod };
    if (scope?.intentGroupId != null) {
      body.intent_group_id = scope.intentGroupId;
    } else if (scope?.applicationId != null && scope?.intentId != null) {
      body.application_id = scope.applicationId;
      body.intent_id = scope.intentId;
    }
    if (dateStart) body.date_start = dateStart;
    if (dateEnd) body.date_end = dateEnd;
    return GeneralService.post(
      "/continuous_learning/trigger",
      JSON.stringify(body),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static takeAction(
    runId: number,
    action: "create_dataset" | "dismiss",
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      `/continuous_learning/runs/${runId}/action`,
      JSON.stringify({ action }),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static rerunRun(
    runId: number,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      `/continuous_learning/runs/${runId}/rerun`,
      JSON.stringify({}),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static deleteRun(
    runId: number,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.delete(
      `/continuous_learning/runs/${runId}`,
      "",
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getFinetuneRun(
    runId: number,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/continuous_learning/finetune-run/${runId}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getActionSummary(
    runId: number,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/continuous_learning/runs/${runId}/action-summary`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static createFinetuneFromRun(
    runId: number,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      `/continuous_learning/runs/${runId}/create-finetune`,
      JSON.stringify({}),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }
}
